import type { ListResponse } from '@warehouse/shared';

import { api } from '../../../lib/api-client';

/**
 * ══ CROSS-MODULE PLACEHOLDER ══════════════════════════════════════════════
 *
 * Production needs to name an ARTICLE (M1 master-data) and a PRODUCTION FLOOR
 * AREA (M2 warehouse-core) when a planner creates an order. Neither register is
 * this module's to build, and neither endpoint exists yet in Wave 1.
 *
 * This file is the typed seam. It declares the shape production expects and
 * NOTHING else — no fallback data, no duplicated register. When M1/M2 land,
 * the only thing that may need touching is the path constants below.
 *
 * The UI degrades honestly: `ArticlePicker` / `AreaPicker` fall back to a plain
 * id field with an explanatory hint when the lookup 404s, so a planner is never
 * blocked and nobody has to pretend the register is there. Production's own
 * bins come from `GET /api/production/floor-bins`, which IS ours.
 *
 * Expected contracts (both are ordinary `listQuery` endpoints):
 *   GET /api/articles?q=&pageSize=       → ListResponse<{ id, articleNo, name, baseUnitCode? }>
 *   GET /api/warehouse/areas?filters[floorRole]=production
 *                                        → ListResponse<{ id, code, name, kind, floorRole }>
 * ══════════════════════════════════════════════════════════════════════════
 */

/** M1 master-data. */
export const ARTICLE_LOOKUP_PATH = '/articles';
/** M2 warehouse-core. */
export const AREA_LOOKUP_PATH = '/warehouse/areas';

export interface ArticleOption {
  id: string;
  articleNo: string;
  name: string;
  baseUnitCode?: string | null;
}

export interface AreaOption {
  id: string;
  code: string;
  name: string;
}

export const articleLookupKey = (search: string): readonly unknown[] => [
  'cross-module',
  'articles',
  search,
];
export const productionAreaLookupKey = (): readonly unknown[] => ['cross-module', 'production-areas'];

export async function fetchArticleOptions(search: string): Promise<ArticleOption[]> {
  const response = await api.get<ListResponse<ArticleOption>>(ARTICLE_LOOKUP_PATH, {
    query: { pageSize: 25, ...(search.trim() ? { q: search.trim() } : {}) },
  });
  return response.items ?? [];
}

export async function fetchProductionAreaOptions(): Promise<AreaOption[]> {
  const response = await api.get<ListResponse<AreaOption>>(AREA_LOOKUP_PATH, {
    query: { pageSize: 100, 'filters[floorRole]': 'production' },
  });
  return response.items ?? [];
}
